import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Users, Loader2, Play, Radio as RadioIcon, ImagePlus, ImageOff, ListPlus, Shuffle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { SelectionBar } from "@/components/SelectionBar";
import { ArtistBio } from "@/components/ArtistBio";
import { SelectMenu, SelectMark, BarButton, LassoArea, crossLabel, useSelection, useSharedSelection } from "@/components/Selection";
import { AddSelectionTo, removeWithPreview } from "@/components/SelectionActions";
import { CoverFinder } from "@/components/CoverFinder";
import { BulkButton, BulkProgress, useBulkJob } from "@/components/BulkArtJob";
import { NearMissReview } from "@/components/NearMissReview";
import { Input } from "@/components/ui/input";
import { Cover } from "@/components/Cover";
import { AlbumGrid } from "@/components/AlbumCard";
import { TrackTable } from "@/components/TrackTable";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { LibrarySwitch, useOwnerView } from "@/components/LibrarySwitch";
import { CopyProgress, CopyToLibraryButton } from "@/components/CopyToLibrary";
import { usePlayer } from "@/context/PlayerContext";
import { usePlaylists } from "@/context/PlaylistsContext";
import { api, artistPhotoUrl, formatApiError } from "@/lib/api";
import { useRestored } from "@/lib/restore";
import { RefreshLibraryButton } from "@/components/RefreshLibraryButton";
import { useCrumb } from "@/lib/trail";
import { useBuildMix } from "@/lib/mix";

const ArtistImage = ({ artist, className = "", iconSize = 36 }) => (artist.photo_version
  ? <img src={artistPhotoUrl(artist.name, artist.photo_version, artist.lib)} alt={artist.name} loading="lazy" className={`aspect-square w-full rounded-full object-cover ${className}`} data-testid="artist-photo" />
  : <Cover albumId={artist.cover_album_id} hasCover={artist.has_cover} version={artist.cover_version} rounded="rounded-full" className={`aspect-square w-full ${className}`} iconSize={iconSize} />);

export function Artists() {
  const { viewing } = useOwnerView();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const setQ = (v) => setParams(v ? { q: v } : {}, { replace: true });
  const [restored, remember] = useRestored();
  const [artists, setArtists] = useState(restored || null);
  const load = () => api.get("/artists").then((r) => setArtists(r.data));
  useEffect(() => { if (!restored) load(); }, [viewing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (artists) remember(artists); }, [artists, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const job = useBulkJob("/artwork/artists/fix-all", { noun: "photos", onDone: load });
  const missing = useMemo(() => (artists || []).filter((a) => !a.photo_version && !["unknown artist", "various artists"].includes(a.name.toLowerCase())).length, [artists]);
  const shown = useMemo(() => (artists || []).filter((a) => a.name.toLowerCase().includes(q.toLowerCase())), [artists, q]);
  const noPhoto = useMemo(() => new Set((artists || []).filter((a) => !a.photo_version).map((a) => a.name.toLowerCase())), [artists]);
  const nearRows = job.running ? [] : (job.status?.skipped || []).filter((x) => x.near?.length && noPhoto.has(x.name.toLowerCase()))
    .map((x) => ({ key: x.name, title: x.name, subtitle: "", near: x.near, item: { name: x.name } }));
  const shownNames = useMemo(() => shown.map((a) => a.name), [shown]);
  const s = useSelection(shownNames, "artists");
  const otherAlbums = [...useSharedSelection("albums").sel];
  const [busy, setBusy] = useState(false);
  const selectedNames = [...s.sel];
  const shownNoPhoto = shown.filter((a) => !a.photo_version && !["unknown artist", "various artists"].includes(a.name.toLowerCase()));
  const removeSelected = async () => {
    setBusy(true);
    try {
      const n = s.count;
      const res = await removeWithPreview({ artists: selectedNames, album_ids: otherAlbums }, crossLabel(n, "artist", otherAlbums.length, "album"));
      if (res) { s.exit(); await load(); }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const findPhotos = async () => {
    const n = shown.filter((a) => s.sel.has(a.name) && !a.photo_version && !["unknown artist", "various artists"].includes(a.name.toLowerCase())).length;
    if (!n) { toast.info("All selected artists already have photos"); return; }
    try {
      await api.post("/artwork/artists/fix-all", { names: selectedNames });
      toast.success(`Looking for photos for ${n} artist${n === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!artists) return <Loader2 className="animate-spin text-zinc-400" />;
  return (
    <div>
      <LibrarySwitch />
      <PageHeader stack eyebrow={`${artists.length} artists${!viewing && missing ? ` · ${missing} without a photo` : ""}`} title="Artists" testid="artists-title">
        <RefreshLibraryButton onDone={() => load()} />
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter artists" data-testid="artist-search-input" className="h-10 w-60 rounded-full bg-white pl-9" />
        </div>
        {!viewing && <SelectMenu s={s} noun="artist" testid="artists-select" picks={[
          { label: "Select artists without a photo", icon: ImageOff, count: shownNoPhoto.length, disabled: !shownNoPhoto.length, testid: "no-photo", onClick: () => s.where((a) => !a.photo_version && !["unknown artist", "various artists"].includes(a.name.toLowerCase()), shown) },
        ]} />}
        {!viewing && (missing > 0 || job.running) && <BulkButton job={job} label="Find photos for all" testid="artists-fix-all-button" />}
      </PageHeader>
      {!viewing && <BulkProgress job={job} noun="photos" testid="artists-fix-all-status" onRetry={job.start} />}
      {!viewing && <NearMissReview rows={nearRows} noun="photo" onSaved={load} testid="near-miss-artists" />}
      <SelectionBar count={s.count + otherAlbums.length} label={`${crossLabel(s.count, "artist", otherAlbums.length, "album")} selected`} hidden={s.hidden} noun="artist" busy={busy} onRemove={removeSelected} onClear={s.none} onSelectAll={!s.allShown ? s.all : null}>
        <AddSelectionTo body={{ artists: selectedNames, album_ids: otherAlbums }} />
        <BarButton onClick={findPhotos} icon={ImagePlus} testid="selection-find-photos" title="Search photos for the selected artists that have none">Find photos</BarButton>
      </SelectionBar>
      {shown.length === 0 ? <EmptyState icon={Users} title="No artists" text="Artists appear here once your library has music." /> : (
        <LassoArea s={s} className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6" testid="artists-lasso">
          {shown.map((a, i) => {
            const on = s.has(a.name);
            return (
            <Link key={a.name} to={`/artist?name=${encodeURIComponent(a.name)}`} data-testid={`artist-card-${i}`} data-sel-id={a.name} aria-pressed={s.mode ? on : undefined}
              onClick={(e) => { if (s.mode) { e.preventDefault(); s.toggle(a.name, e.shiftKey); } }}
              className="group animate-rise" style={{ animationDelay: `${Math.min(i, 20) * 25}ms` }}>
              <div className={`relative overflow-hidden rounded-full border bg-white transition-[box-shadow,border-color] ${on ? "border-zinc-900 ring-2 ring-zinc-900" : "border-zinc-200"}`}>
                <ArtistImage artist={a} className={`transition-transform duration-500 ${s.mode ? (on ? "scale-[0.94]" : "") : "group-hover:scale-105"}`} />
                {s.mode && <SelectMark on={on} className="left-1/2 top-3 -translate-x-1/2" />}
              </div>
              <p className="mt-3 truncate text-center text-sm font-medium">{a.name}</p>
              <p className="text-center font-mono text-[11px] text-zinc-500">{a.album_count} albums · {a.song_count} songs</p>
            </Link>
            );
          })}
        </LassoArea>
      )}
    </div>
  );
}

const ARTISTS_PARENT = { label: "Artists", path: "/artists" };

export function ArtistDetail() {
  const { viewing, canDownload } = useOwnerView();
  const [params] = useSearchParams();
  const name = params.get("name") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [finder, setFinder] = useState(false);
  const [saving, setSaving] = useState(false);
  const p = usePlayer();
  const navigate = useNavigate();
  const { refresh } = usePlaylists();
  const buildMix = useBuildMix();
  useCrumb(data?.name, ARTISTS_PARENT);
  useEffect(() => {
    setData(null); setError(false);
    api.get("/artist", { params: { name } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [name]);
  if (error) return <EmptyState icon={Users} title="Artist not found" text={name} />;
  if (!data) return <Loader2 className="animate-spin text-zinc-400" />;
  const cover = data.albums.find((a) => a.has_cover) || data.albums[0];
  const songCount = data.song_count || 0;
  const own = data.albums.filter((a) => !a.appears_on);
  const appearsOn = data.albums.filter((a) => a.appears_on);
  const allSongs = async () => {
    const { data: r } = await api.get("/tracks", { params: { q: data.name, limit: 1000 } });
    return r.items.filter((t) => (t.artist || "").toLowerCase() === data.name.toLowerCase());
  };
  const playAll = async (shuffle) => {
    try {
      const list = await allSongs();
      if (!list.length) return toast.error("No songs found for this artist");
      p.playTracks(shuffle ? list.sort(() => Math.random() - 0.5) : list);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveAll = async () => {
    setSaving(true);
    try {
      const { data: pl } = await api.post("/artist/playlist", null, { params: { name: data.name } });
      refresh();
      toast.success(pl.updated ? `Updated "${pl.name}"` : `Created "${pl.name}"`, {
        description: `${pl.track_count} song${pl.track_count === 1 ? "" : "s"}, oldest album first.`,
        action: { label: "Open", onClick: () => navigate(`/playlists/${pl.id}`) },
      });
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <div className="animate-rise">
      <div className="mb-2 flex flex-col gap-6 sm:flex-row sm:items-start" data-testid="artist-hero">
        <div className="relative h-28 w-28 shrink-0 md:h-40 md:w-40">
          <div className="h-full w-full overflow-hidden rounded-full border border-zinc-200 bg-white">
            <ArtistImage artist={{ name: data.name, photo_version: data.photo_version, lib: data.lib, cover_album_id: cover?.id, has_cover: cover?.has_cover }} iconSize={44} />
          </div>
          {!viewing && (
            <button type="button" onClick={() => setFinder(true)} data-testid="artist-change-photo" title={data.photo_version ? "Change photo" : "Set photo"}
              className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900 text-white shadow transition-colors hover:bg-zinc-700">
              <ImagePlus size={14} />
            </button>
          )}
        </div>
        {!viewing && <CoverFinder artist={data.name} open={finder} onOpenChange={setFinder} onApplied={(d) => setData((x) => ({ ...x, photo_version: d.photo_version }))} />}
        <div className="min-w-0 flex-1">
      <PageHeader stack eyebrow="Artist" title={data.name} testid="artist-title"
        subtitle={<><span data-testid="artist-counts">{data.albums.length} {data.albums.length === 1 ? "album" : "albums"} · {songCount} {songCount === 1 ? "song" : "songs"}</span>{!viewing && !data.photo_version && <span className="block text-xs text-zinc-400">No artist photo yet — use the camera button to pick or upload one.</span>}<ArtistBio name={data.name} /></>}>
        <button onClick={() => playAll(false)} data-testid="artist-play-all-button" title={`Play all ${songCount} songs`}
          className="flex h-11 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm font-medium text-white transition-transform hover:scale-[1.03]">
          <Play size={15} fill="currentColor" /> Play all
        </button>
        <button onClick={() => playAll(true)} data-testid="artist-shuffle-all-button" title={`Shuffle all ${songCount} songs`}
          className="flex h-11 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900">
          <Shuffle size={15} /> Shuffle
        </button>
        <button onClick={() => p.playTracks(data.top_tracks)} data-testid="artist-play-button"
          className="flex h-11 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900">
          <Play size={15} /> Top tracks
        </button>
        <button onClick={() => buildMix((data.top_tracks || []).slice(0, 10), { artists: [data.name] })} data-testid="artist-build-mix-button" title={`Build a new playlist around ${data.name}`}
          className="flex h-11 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-5 text-sm text-amber-900 transition-colors hover:border-amber-600">
          <Sparkles size={15} /> Build a mix
        </button>
        {!viewing && <button onClick={() => p.startRadio("artist", data.name)} data-testid="artist-radio-button"
          className="flex h-11 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900">
          <RadioIcon size={15} /> Artist radio
        </button>}
        {!viewing && <button onClick={saveAll} disabled={saving} data-testid="artist-all-songs-playlist-button" title={`Create a playlist with all ${songCount} songs by ${data.name}`}
          className="flex h-11 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900 disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <ListPlus size={15} />} All songs → playlist{songCount ? <span className="font-mono text-xs text-zinc-500">{songCount}</span> : null}
        </button>}
        {viewing && canDownload && <CopyToLibraryButton body={{ artist: data.name }} label="Copy all to my library" testid="artist-copy-to-library"
          title={`Save a copy of all ${songCount} songs by ${data.name} into your own library`} />}
      </PageHeader>
        </div>
      </div>
      {viewing && canDownload && <CopyProgress />}
      <h2 className="mb-6 text-lg font-medium">Albums <span className="font-mono text-xs text-zinc-400">{own.length}</span></h2>
      {own.length ? <AlbumGrid albums={own} /> : <p className="text-sm text-zinc-500" data-testid="artist-no-own-albums">No albums of their own — see “Appears on”.</p>}
      {appearsOn.length > 0 && (
        <>
          <h2 className="mb-6 mt-14 text-lg font-medium" data-testid="artist-appears-on-title">Appears on <span className="font-mono text-xs text-zinc-400">{appearsOn.length}</span></h2>
          <AlbumGrid albums={appearsOn} />
        </>
      )}
      <h2 className="mb-4 mt-16 text-lg font-medium">Top tracks</h2>
      <TrackTable tracks={data.top_tracks.slice(0, 20)} readOnly={viewing} />
    </div>
  );
}
